import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  listRulesets,
  publishRuleset,
  activateRuleset,
  validateRuleset,
  getRulesetAdoption,
  type Ruleset,
  type RulesetAdoptionReport,
} from '../../services/rulesets';

// W6 corte 18.B (2026-05-11) — workflow estendido pra Rulesets:
//   1. Validate (dry-run) ANTES de Publish — operador vê erros sem persistir
//   2. Publish (mantido — assinatura Ed25519 + opcional activate)
//   3. Polling adoption pós-publish — `/admin/releases/ruleset-adoption` 5s
//      até timeout 5min OU manual stop OR todas sessões adotaram
//
// Polling pattern: similar ao Deposita PIX modal (feedback_webhook_needs_polling,
// feedback_polling_compare_specific_id). Compara version específica do publish
// recém-feito contra rows.find(r.ruleset_version === target).sessions_24h.

const POLL_INTERVAL_MS = 5000;
const POLL_TIMEOUT_MS  = 5 * 60 * 1000;

const inputCls =
  'w-full bg-[#0d0f12] border border-[#2a2f36] rounded px-3 py-1.5 text-sm text-gray-200 focus:outline-none focus:border-red-500';
const inputMonoCls = inputCls + ' font-mono';

export function Rulesets() {
  // ----- Hooks ANTES de early return -----
  const [items, setItems] = useState<Ruleset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Publish form state
  const [version, setVersion] = useState('');
  const [rulesJson, setRulesJson] = useState('{\n  "rules": []\n}');
  const [activate, setActivate] = useState(true);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Validate dry-run state
  const [validating, setValidating] = useState(false);
  const [validateResult, setValidateResult] = useState<{ valid: boolean; errors?: string[] } | null>(null);

  // Adoption polling state
  const [pollingVersion, setPollingVersion] = useState<string | null>(null);
  const [adoption, setAdoption] = useState<RulesetAdoptionReport | null>(null);
  const [pollElapsed, setPollElapsed] = useState(0);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollStartTsRef = useRef<number>(0);

  async function refresh(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const r = await listRulesets();
      setItems(r.data);
    } catch (e) {
      setError((e as Error).message || 'erro ao listar rulesets');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  function stopPolling(): void {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    setPollingVersion(null);
    setPollElapsed(0);
  }

  function startAdoptionPolling(targetVersion: string): void {
    stopPolling();
    setPollingVersion(targetVersion);
    setAdoption(null);
    setPollElapsed(0);
    pollStartTsRef.current = Date.now();

    const tick = async (): Promise<void> => {
      const elapsed = Date.now() - pollStartTsRef.current;
      setPollElapsed(elapsed);
      if (elapsed >= POLL_TIMEOUT_MS) {
        stopPolling();
        return;
      }
      try {
        const report = await getRulesetAdoption(20);
        setAdoption(report);
        // Stop polling se active_version bate target + ao menos 1 session reportou
        const targetRow = report.rows.find((r) => r.ruleset_version === targetVersion);
        if (
          report.active_version === targetVersion
          && targetRow
          && targetRow.sessions_24h > 0
        ) {
          // Mantém polling pra ver crescer mas adoption já começou — operador pode parar manualmente
        }
      } catch {
        // Backend hiccup — manter polling, próximo tick tenta de novo
      }
    };

    void tick();   // immediate
    pollIntervalRef.current = setInterval(() => void tick(), POLL_INTERVAL_MS);
  }

  async function handleValidate(): Promise<void> {
    let parsed: unknown;
    try {
      parsed = JSON.parse(rulesJson);
    } catch (err) {
      setValidateResult({ valid: false, errors: ['JSON inválido: ' + (err as Error).message] });
      return;
    }
    setValidating(true);
    setValidateResult(null);
    try {
      const r = await validateRuleset({
        version: version.trim() || 'dry-run-preview',
        rules: parsed,
        notes: notes.trim() || undefined,
      });
      setValidateResult(r);
    } catch (e) {
      setValidateResult({ valid: false, errors: [(e as Error).message || 'validate falhou'] });
    } finally {
      setValidating(false);
    }
  }

  async function handlePublish(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!version.trim()) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(rulesJson);
    } catch (err) {
      setError('rules: JSON inválido — ' + (err as Error).message);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const published = await publishRuleset({
        version: version.trim(),
        rules: parsed,
        activate,
        notes: notes.trim() || undefined,
      });
      setVersion('');
      setNotes('');
      setValidateResult(null);
      await refresh();
      // Se activate=true, inicia polling pra ver adoption acontecer
      if (activate) {
        startAdoptionPolling(published.version);
      }
    } catch (e) {
      setError((e as Error).message || 'erro ao publicar');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleActivate(uuid: string, ver: string): Promise<void> {
    if (!confirm(`Ativar ruleset ${ver}? (deactivate o atual)`)) return;
    try {
      await activateRuleset(uuid);
      await refresh();
      startAdoptionPolling(ver);
    } catch (e) {
      setError((e as Error).message || 'erro ao ativar');
    }
  }

  // Helper render — adoption progress
  const pollSecsLeft = pollingVersion
    ? Math.max(0, Math.ceil((POLL_TIMEOUT_MS - pollElapsed) / 1000))
    : 0;

  return (
    <div>
      <header className="mb-6">
        <h2 className="text-2xl font-bold text-gray-200">Rulesets</h2>
        <p className="text-sm text-gray-500 mt-1">
          Sprint 5 / Ciclo 4 — Engine de regras assinada Ed25519. Publish gera
          signature; SDK valida com pubkey embarcada e ativa em runtime sem rebuild.
          <span className="text-gray-400"> W6 18.B: Validate dry-run + adoption polling pós-publish.</span>
        </p>
      </header>

      <form
        onSubmit={handlePublish}
        className="bg-[#161a1f] border border-[#2a2f36] rounded-lg p-4 mb-6 space-y-3"
      >
        <h3 className="text-sm font-medium text-gray-300">Publicar nova versão</h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Version (ex: 2026.05.03-001)</label>
            <input
              type="text"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="2026.05.03-001"
              className={inputMonoCls}
              required
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Notes (audit)</label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Adicionei regra HW BP DR3"
              className={inputCls}
            />
          </div>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Rules JSON</label>
          <textarea
            value={rulesJson}
            onChange={(e) => setRulesJson(e.target.value)}
            rows={8}
            className={inputMonoCls + ' text-xs'}
          />
        </div>

        {/* Validate result panel */}
        {validateResult && (
          <div
            className={`text-sm rounded px-3 py-2 ${
              validateResult.valid
                ? 'bg-emerald-950 border border-emerald-800 text-emerald-300'
                : 'bg-red-950 border border-red-800 text-red-300'
            }`}
          >
            {validateResult.valid ? (
              <span>✓ Schema válido — pode publicar</span>
            ) : (
              <div>
                <strong>✗ Validate falhou:</strong>
                <ul className="mt-1 list-disc list-inside text-xs">
                  {(validateResult.errors || []).map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm text-gray-400">
            <input
              type="checkbox"
              checked={activate}
              onChange={(e) => setActivate(e.target.checked)}
            />
            Ativar imediatamente
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => void handleValidate()}
              disabled={validating || submitting}
              className="bg-[#2a2f36] hover:bg-[#3a4046] disabled:opacity-50 text-gray-200 text-sm font-medium rounded px-4 py-1.5"
            >
              {validating ? 'Validando...' : 'Validate (dry-run)'}
            </button>
            <button
              type="submit"
              disabled={submitting || validating}
              className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-medium rounded px-4 py-1.5"
            >
              {submitting ? 'Publicando...' : 'Publicar'}
            </button>
          </div>
        </div>
      </form>

      {/* Adoption polling panel */}
      {pollingVersion && (
        <div className="bg-[#161a1f] border border-blue-700 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-medium text-blue-300">
                Monitorando adoption de <code className="font-mono">{pollingVersion}</code>
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                Poll a cada {POLL_INTERVAL_MS / 1000}s · timeout em {pollSecsLeft}s · {adoption?.total_sessions_24h ?? 0} sessões 24h
              </p>
            </div>
            <button
              type="button"
              onClick={stopPolling}
              className="text-xs text-gray-400 hover:text-gray-200 transition"
            >
              parar polling
            </button>
          </div>
          {adoption ? (
            <div className="space-y-1">
              {adoption.rows.length === 0 ? (
                <p className="text-xs text-gray-500">
                  Aguardando primeiros heartbeats reportarem versão nova...
                </p>
              ) : (
                adoption.rows.map((r) => {
                  const isTarget = r.ruleset_version === pollingVersion;
                  return (
                    <div
                      key={r.ruleset_version}
                      className={`flex items-center gap-3 text-xs ${
                        isTarget ? 'text-blue-300' : 'text-gray-400'
                      }`}
                    >
                      <code className="font-mono w-64 truncate">{r.ruleset_version}</code>
                      {r.is_active && (
                        <span className="text-emerald-400">[active]</span>
                      )}
                      {isTarget && <span className="text-blue-400">← target</span>}
                      <span className="ml-auto font-mono">
                        {r.sessions_24h} sessões
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          ) : (
            <p className="text-xs text-gray-500">Buscando adoption inicial...</p>
          )}
        </div>
      )}

      {error && (
        <div className="bg-red-950 border border-red-800 text-red-300 text-sm rounded px-3 py-2 mb-4">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-gray-500">Carregando...</p>
      ) : items.length === 0 ? (
        <p className="text-gray-500">Nenhum ruleset publicado.</p>
      ) : (
        <div className="space-y-2">
          {items.map((r) => (
            <div
              key={r.id}
              className="bg-[#161a1f] border border-[#2a2f36] rounded p-3 flex items-center gap-3"
            >
              <code className="text-sm text-gray-300 font-mono">{r.version}</code>
              {r.is_active && (
                <span className="text-xs px-2 py-0.5 rounded border bg-emerald-900 text-emerald-300 border-emerald-700">
                  ATIVO
                </span>
              )}
              <span className="text-xs text-gray-500 flex-1">
                sha256={r.rules_sha256.slice(0, 16)}…
              </span>
              {r.created_at && (
                <span className="text-xs text-gray-500">
                  {new Date(r.created_at).toLocaleString()}
                </span>
              )}
              {!r.is_active && (
                <button
                  type="button"
                  onClick={() => void handleActivate(r.id, r.version)}
                  className="text-xs text-gray-400 hover:text-emerald-400 transition"
                >
                  Ativar
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
